import React, {RefObject, useEffect, useState} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import copy from "copy-to-clipboard";
import {CircularProgress, IconButton} from "@material-ui/core";
import {Reasoning} from "./reasoning";
import {ReasoningDrawer} from "./reasoning_drawer";
import {
  ChatContainer,
  TitleBar,
  ChatContent,
} from "common_components/llm_chat/styled";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import ReplayIcon from "@material-ui/icons/Replay";
import {styled, css} from "styled-components";
import {NewQueryButton} from "common_components/llm_chat/new_query_button";
import CopyButton from "common_components/buttons/copy_button";
import {getMessageUsername} from "./util/get_message_username";
import {processDocumentAmendments} from "utils/process_amendments";
import TypingIndicator from "./typing_indicator";
import {
  Conversation,
  ConversationItem,
  ConversationItemId,
} from "common/types/conversation";
import ThumbsUpDownIcon from "@material-ui/icons/ThumbsUpDown";
import ChatFooter from "./chat_footer";
import {User} from "common/types/user";
import {ClauseId, SectionId} from "common/types/document";
import AmendmentsPanel from "common_components/issue_detail/panels/components/amendments/amendments_panel";
import type {ApplyDocumentAmendmentsFn} from "common_components/issue_detail/panels/components/amendments/amendments_panel";
import type {OnLogAmendmentErrors} from "common_components/issue_detail/panels/components/amendments/amendment_group";
import {
  AppliedAmendments,
  ClauseChangeError,
  ProcessedClauseChange,
  PreviewHeading,
} from "common/flowmaster/types/amendment";
import {responseInProgress} from "./util/item_in_progress";

const Message = styled.div<{$compact?: boolean}>`
  border: 1px solid #e0e0e0;
  padding: ${p => (p.$compact ? "0 0.75em" : "0 1em")};
  max-width: ${p => (p.$compact ? "94%" : "80%")};
  ${p =>
    p.$compact &&
    css`
      font-size: 13px;
      & p {
        margin: 0.5em 0;
      }
    `}
`;

const CurrentUserMessage = styled(Message)`
  border-top-left-radius: 1em;
  border-bottom-left-radius: 1em;
  border-right: none;
  background: #eee;
  text-align: right;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
`;

const ReferencedClausepart = styled.li`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

type ClauseReference = {
  clause_id: number;
  clause_reference_hash: string;
  exact_reference: string;
  reference: string;
  section_id: number;
  section_name: string;
  section_reference: string;
  subclause: string;
};

const OtherUserMessage = styled(Message)`
  border-top-right-radius: 1em;
  border-bottom-right-radius: 1em;
  border-left: none;
`;

const HumanUserMessage = styled(OtherUserMessage)`
  background: #eee;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
`;

const AssistantMessage = styled(OtherUserMessage)<{
  isSendingFeedback?: boolean;
}>`
  background: #e6f4ff;
  border: ${props =>
    props.isSendingFeedback ? "4px solid rgb(80, 178, 255)" : "none"};
  border-left: none;
`;

const MessageHeader = styled.div<{$compact?: boolean}>`
  padding-top: ${p => (p.$compact ? "0.5em" : "1em")};
  width: 100%;
  display: flex;
  gap: 0.5em;
  color: grey;
`;

const MessageFooter = styled(MessageHeader)<{$compact?: boolean}>`
  justify-content: flex-end;
  padding-top: ${p => (p.$compact ? "0.25em" : "0.75em")};
  padding-bottom: ${p => (p.$compact ? "0.5em" : "1em")};
`;

interface MessageRendererOptionalArgs {
  onReferencedClausepartClick;
  documentClauseparts;
  documentHeadings?: PreviewHeading[];
  onInputFeedbackClick;
  isSendingFeedback;
  canSendChatFeedback;
  applyClauseChanges;
  appliedAmendments;
  clauseChangeErrors;
  setClauseChangeError?: (clauseChangeId: string, message: string) => void;
  bulkApplyingIds?: string[];
  onRetryConversationItem;
  isRetrying;
  applyDocumentAmendments?: ApplyDocumentAmendmentsFn;
  getIsTrackAllChangesOn?: () => Promise<boolean>;
  enableTrackAllChanges?: () => Promise<void>;
  removeAppliedAmendments?: (amendmentIds: string[]) => void;
  onOpenReasoning: (message: ConversationItem) => void;
  onLogAmendmentErrors?: OnLogAmendmentErrors;
  itemsById?: Map<ConversationItemId, ConversationItem>;
  compact?: boolean;
}

export interface DocumentClausepart {
  clause_id: ClauseId;
  clause_reference: string;
  section_id: SectionId;
}

function hasMessageFailed(message: ConversationItem) {
  if (!message.llm_meta) {
    return false;
  }

  const {is_complete, has_failed} = message.llm_meta;
  // TODO: stale
  return !is_complete && has_failed;
}

interface CurrentChatProps {
  selectedConversationRef: RefObject<HTMLDivElement>;
  conversation: Conversation;
  generateLlmPrompt: () => void;
  prompt: string;
  setPrompt: React.Dispatch<React.SetStateAction<string>>;
  isLoading: boolean;
  textFieldRef: RefObject<HTMLInputElement>;
  onClose: () => void;
  onNewChat: () => void;
  documentClauseparts: DocumentClausepart[];
  documentHeadings?: PreviewHeading[];
  scrollToClausepart: (id: string) => void;
  updateDocumentConversationPrivacy: (
    id: Conversation["id"],
    privacy: boolean,
  ) => void;
  user: User;
  canMakeQueries: boolean;
  canSendChatFeedback: boolean;
  sendFeedback: (
    conversationItemId: ConversationItemId,
    text: string,
  ) => Promise<unknown>;
  applyClauseChanges: (changes: ProcessedClauseChange[]) => void;
  appliedAmendments: AppliedAmendments;
  clauseChangeErrors: ClauseChangeError[];
  setClauseChangeError?: (clauseChangeId: string, message: string) => void;
  bulkApplyingIds?: string[];
  onRetryConversationItem: (item: ConversationItem) => void;
  applyDocumentAmendments?: ApplyDocumentAmendmentsFn;
  getIsTrackAllChangesOn?: () => Promise<boolean>;
  enableTrackAllChanges?: () => Promise<void>;
  removeAppliedAmendments?: (amendmentIds: string[]) => void;
  onLogAmendmentErrors?: OnLogAmendmentErrors;
  compact?: boolean;
}

function filterVisibleItems(items: ConversationItem[]) {
  return items.filter(
    item => item.role === "user" || !responseInProgress(item),
  );
}

export const CurrentChat = ({
  selectedConversationRef,
  conversation,
  generateLlmPrompt,
  prompt,
  setPrompt,
  isLoading,
  textFieldRef,
  onClose,
  onNewChat,
  documentClauseparts,
  documentHeadings,
  scrollToClausepart,
  updateDocumentConversationPrivacy,
  user,
  canMakeQueries,
  canSendChatFeedback,
  sendFeedback,
  applyClauseChanges,
  appliedAmendments,
  clauseChangeErrors,
  setClauseChangeError,
  bulkApplyingIds,
  onRetryConversationItem,
  applyDocumentAmendments,
  getIsTrackAllChangesOn,
  enableTrackAllChanges,
  removeAppliedAmendments,
  onLogAmendmentErrors,
  compact = false,
}: CurrentChatProps) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const [
    reasoningDrawerMessage,
    setReasoningDrawerMessage,
  ] = useState<ConversationItem | null>(null);
  const recentItem = conversation?.items?.at(-1);
  const typingIndicatorVisible = responseInProgress(recentItem);

  const onReferencedClausepartClick = clausepart => {
    if (!clausepart) {
      return;
    }
    scrollToClausepart(clausepart);
  };

  const handleRetryConversationItem = item => {
    setIsRetrying(true);
    onRetryConversationItem(item);
  };

  useEffect(() => {
    setIsRetrying(false);
  }, [responseInProgress(recentItem)]);

  const updatePrivacy = () => {
    updateDocumentConversationPrivacy(
      conversation.id,
      !conversation.is_private,
    );
  };

  const isCreatedByCurrentUser = conversation?.items?.some(
    item =>
      item.id === conversation?.first_item_id && item.user?.id === user.id,
  );

  const [
    conversationFeedbackItemId,
    setConversationFeedbackItemId,
  ] = useState<null | ConversationItemId>(null);
  const [feedback, setFeedback] = useState<null | string>(null);

  const [
    feedbackSentMsgTimeout,
    setFeedbackSentMsgTimeout,
  ] = useState<ReturnType<typeof setTimeout> | null>(null);

  const onInputFeedbackClick = (item: ConversationItem) => {
    if (conversationFeedbackItemId === item.id) {
      onCancelFeedbackClick();
      return;
    }
    if (feedbackSentMsgTimeout !== null) {
      clearTimeout(feedbackSentMsgTimeout);
      setFeedbackSentMsgTimeout(null);
    }
    setConversationFeedbackItemId(item.id);
  };

  const onCancelFeedbackClick = () => {
    setConversationFeedbackItemId(null);
    setFeedback(null);
  };

  const onSendFeedbackClick = async () => {
    if (conversationFeedbackItemId && feedback && canSendChatFeedback) {
      await sendFeedback(conversationFeedbackItemId, feedback);
      setConversationFeedbackItemId(null);
      setFeedback(null);
      if (textFieldRef.current) {
        textFieldRef.current.value = prompt;
      }
      const timer = setTimeout(() => {
        clearTimeout(timer);
        setFeedbackSentMsgTimeout(null);
      }, 3000);

      setFeedbackSentMsgTimeout(timer);
    }
  };

  useEffect(() => {
    if (feedbackSentMsgTimeout) {
      clearTimeout(feedbackSentMsgTimeout);
      setFeedbackSentMsgTimeout(null);
    }
  }, []);

  const visibleItems = filterVisibleItems(conversation?.items ?? []);
  const itemsById = new Map(
    (conversation?.items ?? []).map(i => [i.id, i] as const),
  );

  return (
    <ChatContainer ref={selectedConversationRef}>
      <TitleBar $compact={compact}>
        <div style={{display: "flex", alignItems: "center"}}>
          <IconButton
            edge="start"
            color="inherit"
            size={compact ? "small" : "medium"}
            onClick={onClose}
          >
            <ChevronLeftIcon />
          </IconButton>
          Conversation
          {isCreatedByCurrentUser && (
            <IconButton
              color="inherit"
              onClick={updatePrivacy}
              size="small"
              style={{padding: 4, marginLeft: "0.5em"}}
              title={
                conversation.is_private
                  ? "Make chat public"
                  : "Make chat private"
              }
            >
              {conversation.is_private ? (
                <VisibilityOffIcon fontSize="small" />
              ) : (
                <VisibilityIcon fontSize="small" />
              )}
            </IconButton>
          )}
        </div>
        {canMakeQueries && (
          <NewQueryButton onNewChat={onNewChat} compact={compact} />
        )}
      </TitleBar>
      <ChatContent ref={selectedConversationRef}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: compact ? "0.5em" : "1em",
            padding: compact ? "0.5em 0" : "1em 0",
          }}
        >
          {visibleItems.map(item =>
            renderMessage(item, user, {
              onReferencedClausepartClick,
              documentClauseparts,
              documentHeadings,
              onInputFeedbackClick: () => onInputFeedbackClick(item),
              isSendingFeedback: item.id === conversationFeedbackItemId,
              canSendChatFeedback,
              applyClauseChanges,
              appliedAmendments,
              clauseChangeErrors,
              setClauseChangeError,
              bulkApplyingIds,
              onRetryConversationItem: handleRetryConversationItem,
              isRetrying,
              applyDocumentAmendments,
              getIsTrackAllChangesOn,
              enableTrackAllChanges,
              removeAppliedAmendments,
              onOpenReasoning: setReasoningDrawerMessage,
              onLogAmendmentErrors,
              itemsById,
              compact,
            }),
          )}
        </div>
        {typingIndicatorVisible && <TypingIndicator />}
      </ChatContent>
      <ChatFooter
        prompt={prompt}
        setPrompt={setPrompt}
        generateLlmPrompt={generateLlmPrompt}
        feedback={feedback}
        setFeedback={setFeedback}
        cancelFeedback={onCancelFeedbackClick}
        sendFeedback={onSendFeedbackClick}
        showFeedbackInput={Boolean(conversationFeedbackItemId)}
        canMakeQueries={canMakeQueries}
        showFeedbackSuccessMsg={Boolean(feedbackSentMsgTimeout)}
        isLoading={isLoading}
        textFieldRef={textFieldRef}
        compact={compact}
      />
      <ReasoningDrawer
        message={reasoningDrawerMessage}
        onClose={() => setReasoningDrawerMessage(null)}
      />
    </ChatContainer>
  );
};

function renderMessage(
  message,
  currentUser,
  args: MessageRendererOptionalArgs,
) {
  const isCurrentUserMessage = message.user.id === currentUser.id;
  const isAssistantRole = message.role === "assistant";
  const messageFailed = hasMessageFailed(message);
  const renderMessageType = getMessageTypeRenderer(
    isCurrentUserMessage,
    isAssistantRole,
    messageFailed,
  );
  return (
    <div
      key={message.id}
      style={{
        display: "flex",
        ...(!isAssistantRole && isCurrentUserMessage
          ? {justifyContent: "end"}
          : {}),
      }}
    >
      {renderMessageType(message, args)}
    </div>
  );
}

function renderFailedMessage(
  message,
  {onRetryConversationItem, isRetrying, compact}: MessageRendererOptionalArgs,
) {
  return (
    <AssistantMessage style={{background: "none"}} $compact={compact}>
      <div
        style={{
          color: "red",
          display: "flex",
          alignItems: "center",
          gap: "0.5em",
        }}
      >
        <ErrorOutlineIcon />
        <p>Something went wrong.</p>
      </div>
      <MessageFooter $compact={compact}>
        {renderMessageTime(message)}
        {isRetrying ? (
          <CircularProgress style={{width: 18, height: 18}} />
        ) : (
          <IconButton
            style={{width: 18, height: 18}}
            onClick={() => onRetryConversationItem(message)}
          >
            <ReplayIcon />
          </IconButton>
        )}
      </MessageFooter>
    </AssistantMessage>
  );
}

function renderAssistantMessage(
  message,
  {
    onReferencedClausepartClick,
    documentClauseparts,
    documentHeadings,
    onInputFeedbackClick,
    isSendingFeedback,
    canSendChatFeedback,
    applyClauseChanges,
    appliedAmendments,
    clauseChangeErrors,
    setClauseChangeError,
    bulkApplyingIds,
    applyDocumentAmendments,
    getIsTrackAllChangesOn,
    enableTrackAllChanges,
    removeAppliedAmendments,
    onOpenReasoning,
    onLogAmendmentErrors,
    itemsById,
    compact,
  }: MessageRendererOptionalArgs,
) {
  const rawAmendments = Array.isArray(message.amendments)
    ? message.amendments
    : [];

  const priorItem = itemsById?.get(message.previous_item_id);
  const userPrompt = priorItem?.role === "user" ? priorItem.text : undefined;

  const processedAmendments = processDocumentAmendments(rawAmendments);
  for (const p of processedAmendments) {
    p.conversationId = message.conversation_id;
    p.userPrompt = userPrompt;
  }

  const clauseReferences = message.metadata.clause_references ?? [];
  const textWithoutReferences = message.text.replace(
    /References:\s(?:Clause\s\d+\.\w,?\s?)+\.*/,
    "",
  );

  return (
    <AssistantMessage isSendingFeedback={isSendingFeedback} $compact={compact}>
      <div>
        {renderMessageInner(textWithoutReferences)}
        {processedAmendments.length
          ? renderAmendments(processedAmendments, {
              documentClauseparts,
              documentHeadings,
              applyClauseChanges,
              appliedAmendments,
              clauseChangeErrors,
              setClauseChangeError,
              bulkApplyingIds,
              applyDocumentAmendments,
              getIsTrackAllChangesOn,
              enableTrackAllChanges,
              onLogAmendmentErrors,
              removeAppliedAmendments,
            })
          : null}
        {renderClauseReferences(
          clauseReferences,
          onReferencedClausepartClick,
          documentClauseparts,
        )}
      </div>
      <MessageFooter style={{justifyContent: "flex-start"}} $compact={compact}>
        <Reasoning message={message} onOpen={() => onOpenReasoning(message)} />
        <CopyButton
          iconStyle={{color: "grey"}}
          onCopyClick={() => copy(message.text)}
          hideCaption={true}
        />
        {canSendChatFeedback && (
          <IconButton
            style={{width: 18, height: 18}}
            onClick={onInputFeedbackClick}
          >
            <ThumbsUpDownIcon />
          </IconButton>
        )}
        {renderMessageTime(message, {marginLeft: "auto"})}
      </MessageFooter>
    </AssistantMessage>
  );
}

function renderMessageInner(text) {
  return <ReactMarkdown children={text} remarkPlugins={[remarkGfm]} />;
}

function renderAmendments(
  amendments,
  {
    documentClauseparts,
    documentHeadings,
    applyClauseChanges,
    appliedAmendments,
    clauseChangeErrors,
    setClauseChangeError,
    bulkApplyingIds,
    applyDocumentAmendments,
    getIsTrackAllChangesOn,
    enableTrackAllChanges,
    removeAppliedAmendments,
    onLogAmendmentErrors,
  }: Pick<
    MessageRendererOptionalArgs,
    | "documentClauseparts"
    | "documentHeadings"
    | "applyClauseChanges"
    | "appliedAmendments"
    | "clauseChangeErrors"
    | "setClauseChangeError"
    | "bulkApplyingIds"
    | "applyDocumentAmendments"
    | "getIsTrackAllChangesOn"
    | "enableTrackAllChanges"
    | "removeAppliedAmendments"
    | "onLogAmendmentErrors"
  >,
) {
  return (
    <AmendmentsPanel
      amendments={amendments}
      applyClauseChanges={applyClauseChanges}
      appliedAmendments={appliedAmendments}
      documentClauseparts={documentClauseparts}
      documentHeadings={documentHeadings}
      clauseChangeErrors={clauseChangeErrors ?? []}
      setClauseChangeError={setClauseChangeError}
      bulkApplyingIds={bulkApplyingIds}
      stacked
      defaultExpanded
      applyDocumentAmendments={applyDocumentAmendments}
      getIsTrackAllChangesOn={getIsTrackAllChangesOn}
      enableTrackAllChanges={enableTrackAllChanges}
      removeAppliedAmendments={removeAppliedAmendments}
      onLogAmendmentErrors={onLogAmendmentErrors}
    />
  );
}

function renderHumanUser(message, {compact}: MessageRendererOptionalArgs) {
  return (
    <HumanUserMessage $compact={compact}>
      <MessageHeader $compact={compact}>
        {getMessageUsername(message)}
      </MessageHeader>
      {renderMessageInner(message.text)}
      <MessageFooter $compact={compact}>
        {renderMessageTime(message)}
      </MessageFooter>
    </HumanUserMessage>
  );
}

function renderCurrentUserMessage(
  message,
  {compact}: MessageRendererOptionalArgs,
) {
  return (
    <CurrentUserMessage $compact={compact}>
      {renderMessageInner(message.text)}
      <MessageFooter $compact={compact}>
        {renderMessageTime(message)}
      </MessageFooter>
    </CurrentUserMessage>
  );
}

function getMessageTypeRenderer(
  isCurrentUserMessage,
  isAssistantRole,
  hasMessageFailed,
) {
  if (isAssistantRole) {
    if (hasMessageFailed) {
      return renderFailedMessage;
    }
    return renderAssistantMessage;
  }

  if (!isCurrentUserMessage) {
    return renderHumanUser;
  }

  return renderCurrentUserMessage;
}

function renderMessageTime(message, extraStyle?: React.CSSProperties) {
  return (
    <div
      style={{
        fontSize: "0.9em",
        lineHeight: "0.9em",
        display: "flex",
        alignItems: "center",
        ...extraStyle,
      }}
    >
      {formatMessageDate(
        message.llm_meta?.last_edited ?? message.creation_date,
      )}
    </div>
  );
}

function formatMessageDate(creationDate) {
  const messageDate = new Date(creationDate);
  const today = new Date();

  const isToday =
    messageDate.getDate() === today.getDate() &&
    messageDate.getMonth() === today.getMonth() &&
    messageDate.getFullYear() === today.getFullYear();

  return isToday
    ? messageDate.toLocaleTimeString()
    : messageDate.toLocaleString();
}

function renderClauseReferences(
  clauseReferences: ClauseReference[],
  onClauseReferenceClick,
  documentClauseparts,
) {
  if (clauseReferences.length === 0) {
    return null;
  }

  const grouped = clauseReferences.reduce((acc, ref) => {
    const key = ref.filename ?? "Current document";
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(ref);
    return acc;
  }, {});

  const files = Object.keys(grouped);
  const showFilename =
    Object.keys(grouped).length > 1 || files[0] !== "Current document";

  return (
    <span style={{color: "#444"}}>
      <div style={{fontStyle: "italic"}}>References:</div>
      {Object.entries(grouped).map(([filename, refs]) => (
        <div key={filename} style={{marginTop: "0.25em"}}>
          {showFilename && (
            <div style={{fontWeight: "bold", marginTop: "0.25em"}}>
              {filename}
            </div>
          )}
          <ul style={{marginBlockStart: "0.25em", marginBlockEnd: "0"}}>
            {refs.map(clauseReference => {
              const subclauseMatch = documentClauseparts.find(
                ({section_id, reference}) =>
                  section_id === clauseReference.section_id &&
                  reference === clauseReference.subclause,
              );
              const exactClauseMatch = documentClauseparts.find(
                ({section_id, reference}) =>
                  section_id === clauseReference.section_id &&
                  reference === clauseReference.exact_reference,
              );
              const referenceClauseMatch = documentClauseparts.find(
                ({section_id, reference}) =>
                  section_id === clauseReference.section_id &&
                  reference === clauseReference.reference,
              );

              const clausepart =
                subclauseMatch ??
                exactClauseMatch ??
                referenceClauseMatch ??
                documentClauseparts.find(
                  ({clause_id}) => clause_id === clauseReference.clause_id,
                );

              return renderClauseReference(
                clauseReference,
                clausepart,
                onClauseReferenceClick,
              );
            })}
          </ul>
        </div>
      ))}
    </span>
  );
}

function renderClauseReference(
  {section_reference, subclause, exact_reference}: ClauseReference,
  clausepart,
  onClick,
) {
  const sectionRef =
    section_reference === "main"
      ? ""
      : section_reference.toLowerCase().replace(/_.*/, "");
  const content = (
    <div>
      <span style={{textTransform: "capitalize"}}>{sectionRef}</span>
      <span>{`${sectionRef ? ": " : "Cl. "} ${
        subclause ? subclause : exact_reference
      }`}</span>
    </div>
  );

  if (clausepart === undefined) {
    return <span>{content}</span>;
  }

  return (
    <ReferencedClausepart onClick={() => onClick(clausepart)}>
      {content}
    </ReferencedClausepart>
  );
}
